import { Box, IconButton } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import { type FC, type MouseEvent, useRef, useState } from "react"

import { ConfirmationDialog } from "@/components/common/Modals"
import type { SignedUrl } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"
import { NotificationType } from "@/schemas/notification"
import { GoodsReceiptUploadsRowDropdownMenu } from "@/screens/WarehouseReceiving/components/GoodsReceiptUploads/GoodsReceiptFileTable/GoodsReceiptUploadsRowDropdownMenu.tsx"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface GoodsReceiptUploadsRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  onRemove: (fileId: string) => Promise<boolean>
}

export const GoodsReceiptUploadsRowActions: FC<GoodsReceiptUploadsRowActionsProps> = ({ row, onRemove }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { downloadFile } = useFileDownload()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
  }

  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const handleStopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation()

  const handleViewDocument = () => {
    window.open(row.original.url, "_blank")
  }

  const handleDownloadDocument = async () => {
    const fileName = row.original.file?.originalName || "download"
    await downloadFile(row.original.url, fileName)
  }

  const handleRemoveDocument = async () => {
    const onRemoveResult = await onRemove(row.original?.file?.fileId as string)
    if (!onRemoveResult) {
      notify({
        message: "Goods receipt file removing failed",
        type: NotificationType.Error,
      })
    } else {
      notify({
        message: "Goods receipt file was removed successfully",
        type: NotificationType.Success,
      })
    }

    setIsConfirmDialogOpen(false)
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef} onClick={handleStopPropagation}>
      <IconButton onClick={handleDropdownOpen} data-testid='goods-receipt-file-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      {isDropdownOpen && (
        <GoodsReceiptUploadsRowDropdownMenu
          anchorEl={anchorElRef.current}
          onClose={handleDropdownClose}
          onView={handleViewDocument}
          onDownload={handleDownloadDocument}
          onRemove={() => setIsConfirmDialogOpen(true)}
        />
      )}
      {isConfirmDialogOpen && (
        <ConfirmationDialog
          title='Remove File'
          content='Are you sure you want to remove this file?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleRemoveDocument}
          isWarning
        />
      )}
    </Box>
  )
}
